import { SubmitHelpers } from '@area2k/use-form';
import { useCallback, useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import Form from '@/form';
import TextField from '@/form/TextField';
import TextAreaField from '@/form/TextAreaField';
import TextSelectField from '@/form/TextSelectField';

import {
  Certificate,
  CertificateInput,
  conflictFrom,
  createCertificate,
  DuplicateConflict,
  errorMessageFrom,
  restoreCertificate,
  updateCertificate,
} from './api';

// The shared badge icon every existing badge uses, and the default for a new one.
const DEFAULT_BADGE_ICON = 'assets/images/workers/certificates/att_badge.svg';

// Mirrors CertificateType in graphy: CERTIFICATE = 0, BADGE = 1.
const TYPE_OPTIONS = [
  { label: 'Badge — can be required by a client', value: '1' },
  { label: 'Certificate — display only', value: '0' },
];

type FormValues = {
  name: string;
  display_name_en: string;
  display_name_es: string;
  description_en: string;
  certificate_type: string;
};

export type IconOption = { key: string; url: string | null };

type Props = {
  certificate?: Certificate | null;
  /**
   * GRAVY-10786: icons already in use across the catalog. There is no asset
   * upload, so "reuse an existing badge's icon" is the mechanism — which is what
   * the epic asked for ("Reuse generic badge icon for now, same as GWA").
   */
  iconOptions?: IconOption[];
  onClose: () => void;
  onSaved: () => void;
};

const BadgeFormModal = ({
  certificate,
  iconOptions = [],
  onClose,
  onSaved,
}: Props) => {
  const isEdit = !!certificate;
  // Kept outside the form state: it is a click-to-pick control, not a field.
  // GRAVY-10798: a duplicate can clash with a soft-deleted row. Hold it so the
  // admin is told that, and offered Restore rather than a dead end.
  const [conflict, setConflict] = useState<DuplicateConflict | null>(null);
  const [iconKey, setIconKey] = useState<string | null>(
    certificate ? certificate.image_key : DEFAULT_BADGE_ICON,
  );

  const initialValues: FormValues = {
    name: certificate?.name ?? '',
    display_name_en: certificate?.display_name_en ?? '',
    display_name_es: certificate?.display_name_es ?? '',
    description_en: certificate?.description_en ?? '',
    certificate_type: certificate
      ? String(certificate.certificate_type === 'BADGE' ? 1 : 0)
      : '1',
  };

  const handleSubmit = useCallback(
    async (values: FormValues, { setFormError }: SubmitHelpers) => {
      if (!values.name || values.name.trim() === '') {
        setFormError('nameRequired', {
          title: 'Name required',
          message: 'Give the badge a name.',
          status: 'danger',
        });
        return;
      }

      const payload: CertificateInput = {
        name: values.name.trim(),
        display_name_en: values.display_name_en.trim() || values.name.trim(),
        display_name_es: values.display_name_es.trim(),
        description_en: values.description_en.trim(),
        certificate_type: Number(values.certificate_type),
        image_key: iconKey,
      };

      try {
        if (isEdit && certificate) {
          await updateCertificate(certificate.id, payload);
        } else {
          await createCertificate(payload);
        }
        onSaved();
        onClose();
      } catch (err: any) {
        const clash = conflictFrom(err);
        if (clash?.deleted) {
          // Deleted clash: say so, and offer the way out. No form error — the
          // banner below carries the message and the Restore action.
          setConflict(clash);
          return;
        }
        setConflict(null);
        setFormError('saveFailed', {
          title: isEdit ? 'Could not save' : 'Could not create',
          message: errorMessageFrom(err, 'Please try again.'),
          status: 'danger',
        });
      }
    },
    [certificate, iconKey, isEdit, onClose, onSaved],
  );

  const handleRestoreConflict = useCallback(async () => {
    if (!conflict) return;
    await restoreCertificate(conflict.id);
    onSaved();
    onClose();
  }, [conflict, onClose, onSaved]);

  return (
    <Modal
      disableClickout
      size="sm"
      title={isEdit ? 'Edit badge' : 'New badge'}
      onRequestClose={onClose}
    >
      <Card.Section>
        {conflict && (
          <Stack vertical gap={8} style={{ marginBottom: 16 }}>
            <Small color="danger">
              A{' '}
              {conflict.certificate_type === 'BADGE' ? 'badge' : 'certificate'}{' '}
              named “{conflict.name}” already exists but was deleted. Restore it
              instead of creating a duplicate.
            </Small>
            <Stack justify="start">
              <Button
                a11yLabel={`Restore ${conflict.name}`}
                label={`Restore “${conflict.name}”`}
                onClick={handleRestoreConflict}
              />
            </Stack>
          </Stack>
        )}
        <Form initialValues={initialValues} onSubmit={handleSubmit}>
          <TextField
            autoFocus
            required
            showRequiredIndicator
            fieldId="name"
            label="Name"
            placeholder="e.g. Texas Food Handler"
          />
          <TextSelectField
            fieldId="certificate_type"
            label="Type"
            options={TYPE_OPTIONS}
          />
          <TextField
            fieldId="display_name_en"
            label="Display name (English)"
            placeholder="Defaults to the name above"
          />
          <TextField
            fieldId="display_name_es"
            label="Display name (Spanish)"
            placeholder="Shown to Spanish-speaking workers"
          />
          <TextAreaField
            fieldId="description_en"
            label="Description"
            placeholder="What this badge certifies"
          />
          <Stack vertical gap={8}>
            <Small color="light">Icon</Small>
            <Stack gap={8} wrap>
              {[
                { key: DEFAULT_BADGE_ICON, url: null },
                ...iconOptions.filter((o) => o.key !== DEFAULT_BADGE_ICON),
              ].map((option) => {
                const selected = iconKey === option.key;
                return (
                  <button
                    key={option.key}
                    aria-label={`Use icon ${option.key.split('/').pop()}`}
                    aria-pressed={selected}
                    onClick={() => setIconKey(option.key)}
                    style={{
                      width: 44,
                      height: 44,
                      padding: 4,
                      cursor: 'pointer',
                      background: '#fff',
                      borderRadius: 6,
                      border: selected
                        ? '2px solid #45a735'
                        : '1px solid #E5E7EB',
                    }}
                    type="button"
                  >
                    {option.url ? (
                      <img
                        alt=""
                        src={option.url}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <Small color="lighter">A</Small>
                    )}
                  </button>
                );
              })}
              <button
                aria-label="Use no icon"
                aria-pressed={iconKey === null}
                onClick={() => setIconKey(null)}
                style={{
                  width: 44,
                  height: 44,
                  cursor: 'pointer',
                  background: '#fff',
                  borderRadius: 6,
                  border:
                    iconKey === null
                      ? '2px solid #45a735'
                      : '1px solid #E5E7EB',
                }}
                type="button"
              >
                <Small color="lighter">None</Small>
              </button>
            </Stack>
          </Stack>

          <Stack vertical gap={12}>
            <Small color="light">
              Only badges can be set as a client or job requirement. Creating a
              badge does not grant it to anyone — assign it to workers from
              their profile before adding it to a client, or jobs requiring it
              will match nobody.
            </Small>
            <Stack justify="end" gap={8}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                type="button"
                onClick={onClose}
              />
              <Button
                a11yLabel={isEdit ? 'Save badge' : 'Create badge'}
                label={isEdit ? 'Save' : 'Create'}
                type="submit"
              />
            </Stack>
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default BadgeFormModal;
